import { useEffect, useMemo, useState } from "react";
import { Play, Instagram, Youtube, AtSign, Video } from "lucide-react";
import { fetchClips } from "../lib/api";
import { toEmbedSrc } from "../lib/embed";
import Reveal from "./Reveal";
import PlayFeaturedReelButton from "./PlayFeaturedReelButton";

const CATEGORIES = [
    "All",
    "On Set",
    "Behind The Camera",
    "Commercial",
    "Narrative",
    "Comedy",
    "AI Experiments",
    "Archive",
    "Experimental",
];

const SOURCE_ICON = {
    threads: AtSign,
    instagram: Instagram,
    youtube: Youtube,
    vimeo: Video,
    upload: Play,
};

const sourceLabel = (s) =>
    ({
        threads: "Threads",
        instagram: "Instagram",
        youtube: "YouTube",
        vimeo: "Vimeo",
        upload: "Studio",
    })[s] || "Studio";

/**
 * RecentVisuals — premium "contact sheet" feed of clips.
 * Reveals an embed in a modal on click. Curated, lazy-loaded.
 */
export const RecentVisuals = () => {
    const [clips, setClips] = useState([]);
    const [active, setActive] = useState("All");
    const [open, setOpen] = useState(null); // clip currently shown in modal

    useEffect(() => {
        fetchClips({ limit: 36 })
            .then(setClips)
            .catch(() => setClips([]));
    }, []);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") setOpen(null);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open]);

    const filtered = useMemo(() => {
        if (active === "All") return clips;
        return clips.filter((c) => c.category === active);
    }, [clips, active]);

    if (clips.length === 0) return null;

    const embed = open ? toEmbedSrc(open.embed_url) : null;

    return (
        <section
            data-testid="recent-visuals-section"
            className="relative border-t border-border"
        >
            <div className="mx-auto max-w-[1500px] px-6 sm:px-10 lg:px-14 py-32 sm:py-40">
                <div className="flex flex-wrap items-end justify-between gap-8 mb-12">
                    <Reveal>
                        <div>
                            <p className="text-label text-muted-foreground mb-4">
                                Contact Sheet — 2024 / 25
                            </p>
                            <h2 className="font-serif-display text-3xl sm:text-5xl tracking-tight max-w-2xl">
                                Recent visuals,{" "}
                                <span className="italic text-muted-foreground">
                                    on-set notes, ongoing work.
                                </span>
                            </h2>
                            <p className="mt-6 max-w-xl text-base text-foreground/70 leading-relaxed">
                                A living archive — clips, stills, and small
                                experiments from the field. Updated as the work
                                happens.
                            </p>
                            <Reveal delay={250} className="mt-8">
                                <PlayFeaturedReelButton />
                            </Reveal>
                        </div>
                    </Reveal>

                    <Reveal delay={150}>
                        <div
                            data-testid="recent-visuals-filters"
                            className="flex flex-wrap gap-x-6 gap-y-2 max-w-md"
                        >
                            {CATEGORIES.map((c) => (
                                <button
                                    key={c}
                                    type="button"
                                    onClick={() => setActive(c)}
                                    data-testid={`recent-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                                    className={`text-label transition-colors duration-500 ${
                                        active === c
                                            ? "text-foreground"
                                            : "text-muted-foreground hover:text-foreground"
                                    }`}
                                >
                                    {c}
                                </button>
                            ))}
                        </div>
                    </Reveal>
                </div>

                {/* Contact-sheet grid: tight gutters, varied row heights for editorial feel */}
                <div
                    data-testid="recent-visuals-grid"
                    className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4"
                >
                    {filtered.map((c, i) => {
                        const Icon = SOURCE_ICON[c.source] || Play;
                        // Editorial variation: every 5th tile spans 2 rows, every 7th spans 2 cols.
                        const tall = i % 5 === 0;
                        const wide = i % 7 === 6;
                        const isNew = (() => {
                            if (!c.created_at) return false;
                            const age =
                                Date.now() - new Date(c.created_at).getTime();
                            return age >= 0 && age <= 24 * 60 * 60 * 1000;
                        })();
                        return (
                            <Reveal
                                key={c.id}
                                delay={(i % 6) * 60}
                                className={`${tall ? "row-span-2" : ""} ${wide ? "md:col-span-2" : ""}`}
                            >
                                <button
                                    type="button"
                                    onClick={() => setOpen(c)}
                                    data-testid={`recent-tile-${c.id}`}
                                    className={`group relative block w-full overflow-hidden bg-muted text-left ${
                                        tall ? "aspect-[3/4] md:aspect-[3/5]" : "aspect-[4/3]"
                                    }`}
                                >
                                    {c.thumbnail_url ? (
                                        <img
                                            src={c.poster_url || c.thumbnail_url}
                                            alt={c.title || c.caption || ""}
                                            loading="lazy"
                                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.05] opacity-90 group-hover:opacity-100"
                                        />
                                    ) : (
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <Icon
                                                size={28}
                                                strokeWidth={1.25}
                                                className="text-muted-foreground"
                                            />
                                        </div>
                                    )}

                                    {/* Frame number — film contact-sheet feel */}
                                    <span className="absolute top-3 left-3 z-10 text-[10px] tracking-[0.3em] uppercase text-foreground/70">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>

                                    {/* Featured / New small markers */}
                                    {(c.featured || isNew) && (
                                        <span className="absolute bottom-3 left-3 z-10 inline-flex items-center gap-2">
                                            {c.featured && (
                                                <span
                                                    data-testid={`recent-featured-${c.id}`}
                                                    className="text-[9px] tracking-[0.28em] uppercase text-foreground/70 border border-foreground/30 px-2 py-0.5"
                                                >
                                                    Featured
                                                </span>
                                            )}
                                            {isNew && (
                                                <span
                                                    data-testid={`recent-new-${c.id}`}
                                                    className="text-[9px] tracking-[0.28em] uppercase text-foreground/60 border border-foreground/25 px-2 py-0.5"
                                                >
                                                    New
                                                </span>
                                            )}
                                        </span>
                                    )}

                                    {/* Source tag */}
                                    <span className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase text-foreground/70">
                                        <Icon size={12} strokeWidth={1.5} />
                                        {sourceLabel(c.source)}
                                    </span>

                                    {/* Hover info */}
                                    <div className="absolute inset-x-0 bottom-0 z-[5] p-4 sm:p-5 bg-gradient-to-t from-black/90 via-black/50 to-transparent opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-700">
                                        {(c.title || c.project_name) && (
                                            <p className="font-serif-display text-lg sm:text-xl leading-tight">
                                                {c.title || c.project_name}
                                            </p>
                                        )}
                                        <p className="mt-1 text-[11px] tracking-[0.2em] uppercase text-foreground/70">
                                            {[c.year, c.role, c.category]
                                                .filter(Boolean)
                                                .join("  ·  ")}
                                        </p>
                                        {c.caption && (
                                            <p className="mt-2 text-xs text-foreground/70 leading-relaxed line-clamp-2">
                                                {c.caption}
                                            </p>
                                        )}
                                    </div>
                                </button>
                            </Reveal>
                        );
                    })}
                </div>

                {filtered.length === 0 && (
                    <p className="mt-10 text-center text-muted-foreground text-sm">
                        Nothing in this category — yet.
                    </p>
                )}
            </div>

            {/* MODAL */}
            {open && (
                <div
                    data-testid="recent-modal"
                    className="fixed inset-0 z-[80] bg-black/95 backdrop-blur-xl flex items-center justify-center p-4"
                    onClick={() => setOpen(null)}
                >
                    <button
                        type="button"
                        onClick={() => setOpen(null)}
                        data-testid="recent-modal-close"
                        className="absolute top-6 right-6 text-label text-foreground/80 hover:text-foreground"
                    >
                        Close ×
                    </button>

                    <div
                        className="w-full max-w-4xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="aspect-video bg-muted overflow-hidden cinematic-shadow">
                            {embed ? (
                                <iframe
                                    title={open.title || "Clip"}
                                    src={embed}
                                    className="w-full h-full"
                                    allow="autoplay; fullscreen; picture-in-picture"
                                    allowFullScreen
                                />
                            ) : open.thumbnail_url ? (
                                <img
                                    src={open.thumbnail_url}
                                    alt={open.title || ""}
                                    className="w-full h-full object-contain bg-black"
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center p-10 text-center">
                                    <p className="font-serif-display text-2xl text-muted-foreground max-w-lg">
                                        Paste a Threads, Instagram, Vimeo, or
                                        YouTube link in&nbsp;
                                        <span className="italic">
                                            Admin → Clips
                                        </span>{" "}
                                        to bring this frame to life.
                                    </p>
                                </div>
                            )}
                        </div>
                        <div className="mt-5 flex items-baseline justify-between gap-6">
                            <div>
                                <p className="font-serif-display text-2xl tracking-tight">
                                    {open.title || "Untitled"}
                                </p>
                                {open.caption && (
                                    <p className="mt-1 text-sm text-muted-foreground max-w-xl leading-relaxed">
                                        {open.caption}
                                    </p>
                                )}
                            </div>
                            <p className="text-label text-muted-foreground shrink-0">
                                {[open.year, open.role, open.category]
                                    .filter(Boolean)
                                    .join("  ·  ")}
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default RecentVisuals;
